import {
  NO_LIMIT,
  REMBLE_API_URL,
  WP_API_URL,
  learnDashGET,
  cleanCourseItem,
  practicesGET,
} from '.'
import { getMediaById } from './Media'
import { app } from 'config'

const LMS_TYPE_PREFIX = 'sfwd-'
const LMS_TYPE_QUIZ = `${LMS_TYPE_PREFIX}quiz`
const LMS_TYPE_TOPIC = `${LMS_TYPE_PREFIX}topic`
const LMS_TYPE_LESSON = `${LMS_TYPE_PREFIX}lesson`
const LMS_TYPE_LESSONS = `${LMS_TYPE_PREFIX}lessons`
const LMS_TYPE_COURSE = `${LMS_TYPE_PREFIX}course`
const LMS_TYPE_COURSES = `${LMS_TYPE_PREFIX}courses`

const COURSE_FIELD_COURSE_LENGTH = 'course_length'

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Merges a LearnDash Lessons array with a LearnDash Steps object
 * @param {Array} steps The Steps object to merge the Lessons array with
 * @param {Array} lessons The Lessons Array to merge with the Steps object
 */
const _mergeSectionStepsAndLessons = (steps, lessons) =>
  steps.sections.map(section => ({
    ...section,
    lessons: section.steps.map(step =>
      cleanCourseItem(lessons.find(lesson => lesson.id === step))
    )
  }))

/**
 * Private helper to return a clean and consistent author object
 * @param {Object} author The WP author post data to parse, clean and combine
 */
const _cleanAuthorData = author => ({
  // Direct author query
  id: author.id,
  name: author.title.rendered,
  bio: author.content.rendered,
  authorImage: author.featured_image_thumbnail_url
})

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Retrieves the cache key for the passed in lesson ID
 * @param {Number} lessonId The ID of the lesson
 * @returns String containing the cache key
 */
const getLessonCacheKey = lessonId => `lesson_${lessonId}`

/**
 * Retrieves the cache key for the passed in lesson ID and topic ID.
 * Prefixes with the lesson, because the lesson page will have a 0 value for the topic id
 * @param {Number} lessonId The ID of the lesson
 * @param {Number} topicId The ID of the topic
 * @returns String containing the cache key
 */
const getTopicCacheKey = (lessonId, topicId = 0) =>
  `lesson_${lessonId}_topic_${topicId}`

/**
 * Retrieves all courses available. Accepts params to filter the result as needed.
 * Automatically sorts by course length, if available
 * @param {Object} params Course parameters to filter result set.
 * See https://developers.learndash.com/rest-api/v2/v2-courses/ for more info.
 */
const getCourses = async params => {
  const url = `${REMBLE_API_URL}/lms/`
  const courses = await learnDashGET(
    url,
    { uri: `/${LMS_TYPE_PREFIX}courses`, per_page: NO_LIMIT },
    params
  )

  return courses
    .map(course => cleanCourseItem(course))
    .sort(
      (a, b) =>
        parseInt(tryGetCourseField(a, 'course_length')) -
        parseInt(tryGetCourseField(b, 'course_length'))
    )
}

/**
 * Retrieves all course categories available, or an empty array if none are found
 */
const getCourseCategories = async () => {
  let categories = []

  // Use the LMS wp-json proxy to query the WP API
  const url = `${REMBLE_API_URL}/lms/wp-json`
  const result = await learnDashGET(url, {
    uri: `/wp/v2/ld_course_category`,
    type: LMS_TYPE_COURSES,
    hide_empty: 1,
    include_children: 1,
    per_page: 100
  })

  // Only return the result if it's an array
  if (result?.length) categories = result

  return categories
}

/**
 *
 * @returns All the WP categories
 */
const getWpCategories = async (parent) => {
  let categories = []

  const url = `${WP_API_URL}/categories/`
  const result = await learnDashGET(url, {
    per_page: 99,
    parent: parent ?? 0
  })

  if (result?.length) categories = result

  return categories
}

/**
 * @return WP category by slug
 */
const getCategoryById = async (id) => {
  const url = `${WP_API_URL}/categories/${id}`

  const result = await learnDashGET(url)

  if (result) return result

  return undefined
}

/**
 * Retrieves recommended courses for the current user
 * @returns An array of recommended courses
 */
const getRecommendedCourses = async () => {
  const url = `${REMBLE_API_URL}/lms/recommended`
  const courses = await learnDashGET(url)

  // Return an empty array if there are no recommended courses
  if (!courses?.length) return []

  return courses
    .map(course => cleanCourseItem(course))
    .sort(
      (a, b) =>
        parseInt(tryGetCourseField(a, 'course_length')) -
        parseInt(tryGetCourseField(b, 'course_length'))
    )
}
/**
 * Retrieves all courses that match the passed in terms.
 * @param {String} terms Search terms to search for within the course
 * See https://developers.learndash.com/rest-api/v2/v2-courses/ for more info.
 */
const searchCourses = async terms => {
  const url = `${REMBLE_API_URL}/lms/`
  const courses = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}courses`,
    terms
  })

  return courses.map(course => cleanCourseItem(course))
}

/**
 * Retrieves a Course given its ID. See https://developers.learndash.com/rest-api/v2/v2-courses/ for more info.
 * @param {Number} courseId The Course ID to lookup.
 */
const getCourseById = async courseId => {
  const url = `${REMBLE_API_URL}/lms/`
  const course = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}courses/${courseId}`
  })

  return course.error ? course : cleanCourseItem(course)
}

/**
 * Tries to retrieve the given custom field from the passed in course object, or return the default 'empty' value
 * @param {Obj} courseObject Course object to try. Can be Topic, Lesson, Quiz or Course
 * @param {String} field Custom field to test/try to get from the Course
 * @param {String|*} empty The default value to return if the field is empty or doesn't exist
 */
const tryGetCourseField = (courseObject, field, empty = '') =>
  courseObject && courseObject.acf_extended && courseObject.acf_extended[field]
    ? courseObject.acf_extended[field]
    : empty

/**
 * Tries to retrieve the media URL for the passed in mediaId, or returns a placeholder if it fails
 * @param {Number} mediaId The mediaId for which to retrieve the source url
 */
const tryGetCourseMedia = async mediaId => {
  // Set fallback media
  let media = {
    source_url:
      'https://fpoimg.com/300x250?text=Preview&bg_color=000000&text_color=111111',
    alt_text: 'FPO'
  }

  // Try retrieving media
  try {
    const mediaResult = await getMediaById(mediaId)

    // If we pullled media successfully, let's assign it
    if (mediaResult && mediaResult.source_url) media = mediaResult
  } catch (e) {
    // Swallow media exceptions, typically 403 forbidden
  }

  return media
}

/**
 * Tries to parse the passed in "course steps" object and retrieve the topic data for the lesson with the passed in lesson ID
 * @param {Number} lessonId The ID of the lesson to lookup
 * @param {Object} courseSteps The course steps object. See `getCourseSteps` or `getFullCourseById`
 * @returns The topic data object or an empty object `{}`
 */
const tryGetTopicDataForLessonFromCourseSteps = (lessonId, courseSteps) => {
  let data = {}

  try {
    data = courseSteps.h[LMS_TYPE_LESSONS][lessonId][LMS_TYPE_TOPIC]
  } catch (error) {} // Swallow exceptions

  return data
}

/**
 * Tries to parse the passed in "course steps" object and retrieve the topics for the lesson with the passed in lesson ID
 * ordered by the course steps order
 * @param {Number} lessonId The ID of the lesson to lookup
 * @param {Object} course The course object. See `getFullCourseById`
 * @returns The topics array or an empty array `[]`
 */
const tryGetOrderedTopicsForLessonFromCourse = (lessonId, course) => {
  let topics = []

  try {
    // Get the topic outline for the lesson
    const topicData = tryGetTopicDataForLessonFromCourseSteps(
      lessonId,
      course.steps
    )

    // Course order is an array of all course items in order
    const courseOrder = course.steps.co

    // Get the topic IDs from the topic data
    const topicIds = Object.keys(topicData)

    // Loop through the course order and add the topics in order
    courseOrder.every(item => {
      // Split the item into type and ID
      const [, id] = item.split(':')

      // If the item is a topic and it's in the topic data, add it to the topics array
      if (topicIds.includes(id)) {
        let topic = topicData[id]

        // Loop through topics and merge with api result
        const foundTopic = course.topics.find(
          topic => topic.id === parseInt(id)
        )

        // If we found a topic, merge it with the topic data
        if (foundTopic) topic = { ...topic, ...foundTopic }

        topics.push(topic)
      }

      // Break out of the loop if we've found all the topics
      return topics.length !== topicIds.length
    })
  } catch (error) {
    console.error(error)
  } // Swallow exceptions

  return topics
}

/**
 * Tries to parse the passed in "course steps" object and get the count of topics in the lesson with the passed in ID
 * @param {Number} lessonId The ID of the lesson to lookup
 * @param {Object} courseSteps The course steps object. See `getCourseSteps` or `getFullCourseById`
 * @returns The number of topics in the lesson or 0
 */
const tryGetTopicCountForLessonFromCourseSteps = (lessonId, courseSteps) => {
  const topicData = tryGetTopicDataForLessonFromCourseSteps(
    lessonId,
    courseSteps
  )

  return Object.entries(topicData).length
}

/**
 * Retrieves the Steps for a Course given its ID. See https://developers.learndash.com/rest-api/v2/v2-course-steps/ for more info.
 * @param {Number} courseId The Course ID to lookup steps for.
 */
const getCourseSteps = async courseId => {
  const url = `${REMBLE_API_URL}/lms/`
  return learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}courses/${courseId}/steps`,
    per_page: NO_LIMIT
  })
}

/**
 * Retrieves the Lessons for a Course given its ID. See https://developers.learndash.com/rest-api/v2/v2-lessons/ for more info.
 * @param {Number} courseId The Course ID to lookup Lessons for.
 */
const getCourseLessons = async courseId => {
  const url = `${REMBLE_API_URL}/lms/`
  const lessons = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}lessons`,
    course: courseId,
    per_page: NO_LIMIT
  })

  return lessons.map(lesson => cleanCourseItem(lesson))
}

/**
 * Retrieves all the topics for a Course given its ID. See https://developers.learndash.com/rest-api/v2/v2-lessons/ for more info.
 * @param {Number} courseId The Course ID to lookup Topics for.
 */
const getCourseTopics = async courseId => {
  const url = `${REMBLE_API_URL}/lms/`
  const topics = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}topic`,
    course: courseId,
    per_page: NO_LIMIT
  })

  return topics.map(lesson => cleanCourseItem(lesson))
}

/**
 * Retrieves a Lesson given its ID. See https://developers.learndash.com/rest-api/v2/v2-lessons/ for more info.
 * @param {Number} lessonId The Lesson ID to lookup.
 */
const getLessonById = async lessonId => {
  const url = `${REMBLE_API_URL}/lms/`
  let lesson = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}lessons/${lessonId}`
  })

  lesson = {
    ...lesson,
    video_url: tryGetCourseField(lesson, 'video_url')
  }

  return cleanCourseItem(lesson)
}

/**
 * Retrieves an array of Lessons given an array of Lesson IDs. See https://developers.learndash.com/rest-api/v2/v2-lessons/ for more info.
 * If no lessons are found, an empty array is returned.
 * @param {Array[Number]} lessonIds The Lesson IDs to lookup.
 */
const getLessonsByIds = async lessonIds => {
  const url = `${REMBLE_API_URL}/lms/`
  const lessons = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}lessons`,
    include: lessonIds.join(',')
  })

  // Return an empty array if there are no recommended courses
  if (!lessons?.length) return []

  // Clean up the lessons
  return lessons.map(lesson => cleanCourseItem(lesson))
}

/**
 * Retrieves the Topics for a Lesson and Course given their IDs. See https://developers.learndash.com/rest-api/v2/v2-topics/ for more info.
 * @param {Number} lessonId The Lesson ID for which to lookup Topics.
 * @param {Number} courseId The Course ID for which to lookup Topics.
 */
const getLessonTopics = async (lessonId, courseId) => {
  const url = `${REMBLE_API_URL}/lms/`
  const topics = await learnDashGET(url, {
    uri: `/${LMS_TYPE_PREFIX}topic`,
    course: courseId,
    lesson: lessonId,
    per_page: 50
  })

  return topics.map(topic => cleanCourseItem(topic))
}

/**
 * Retrieves a Topic given its ID. See https://developers.learndash.com/rest-api/v2/v2-topics/ for more info.
 * @param {Number} topicId The Topic ID to lookup.
 */
const getTopicById = async topicId => {
  const url = `${REMBLE_API_URL}/lms/`
  return learnDashGET(url, { uri: `/${LMS_TYPE_PREFIX}topic/${topicId}` })
}

/**
 * Retrieves the Course details, Steps and Lessons for a Course given its ID. See https://developers.learndash.com/rest-api/v2/v2-quizzes/ for more info.
 * This method makes multiple REST calls and then merges the results into an object
 * @param {Number} courseId The Course ID to lookup.
 */
const getFullCourseById = async courseId => {
  // TODO: Find more performant method for loading courses
  // Load course details

  const [course, steps, lessons, topics] = await Promise.all([
    getCourseById(courseId),
    getCourseSteps(courseId),
    getCourseLessons(courseId),
    getCourseTopics(courseId)
  ])

  // Clean up author data
  let authors = tryGetCourseField(course, 'content_author') ?? []

  // Stitch the course together
  let fullCourse = {
    ...course,
    steps,
    lessons,
    topics,
    authors,
    sections: _mergeSectionStepsAndLessons(steps, lessons)
  }

  // Fallback for empty section headings (e.g., no section headings added in LearnDash)
  if (!fullCourse.sections || !fullCourse.sections.length) {
    // Get default section title, if needed
    const sectionTitle = `${tryGetCourseField(
      fullCourse,
      'course_length'
    )} Session Course`

    fullCourse.sections = [
      { ID: 0, post_title: sectionTitle, lessons: fullCourse.lessons }
    ]
  }

  return fullCourse
}

/**
 * Retrieves detailed author bio info
 * @param {Number} authorId The ID of the author to retrieve
 * @returns an object with an error or the author data
 */
const getFullAuthorById = async authorId => {
  // Return early if no author ID is provided
  if (!authorId) return

  const url = `${WP_API_URL}/remble_author/${authorId}`
  try {
    const author = await practicesGET(url)

    // Return early if there's an error
    if (author.error) return author

    if (author) return _cleanAuthorData(author)
  } catch (e) {
    console.error('Error retrieving author data', e)
  }

  return null
}

/**
 * Helper that checks to see whether a given course is free
 * @param {Object} course The course object to check
 * @returns Boolean value indicating whether or not the course is free
 */
const isFreeCourse = course => {
  // Default to false
  let isFree = false

  // Check for free course
  try {
    isFree = course?.ld_course_category?.includes(
      parseInt(app.plans.free_category_id)
    )
  } catch (e) {
    // Silently fail
    console.error(e)
  }

  return isFree
}

/**
 * Helper that checks to see whether a given topic is free
 * @param {Object} topic The topic object to check
 * @returns Boolean value indicating whether or not the topic is free
 */
const isFreeTopic = topic => {
  // Default to false
  let isFree = false

  // Check for free course
  try {
    isFree = topic?.ld_topic_category?.includes(
      parseInt(app.plans.free_topic_id)
    )
  } catch (e) {
    // Silently fail
    console.error(e)
  }

  return isFree
}

/**
 * Helper that checks to see whether a given category ID is the free category
 * @param {Number} categoryId The category ID to check
 * @returns Boolean value indicating whether or not the category ID is the free category
 */
const isFreeCategory = categoryId => {
  return categoryId === parseInt(app.plans.free_category_id)
}

/**
 * Helper that checks to see whether a given category ID is the featured category
 * @param {Number} categoryId The category ID to check
 * @returns Boolean value indicating whether or not the category ID is the featured category
 */
const isFeaturedCategory = categoryId => {
  return categoryId === parseInt(app.wp_api.featured_category_id)
}

export {
  getLessonCacheKey,
  getTopicCacheKey,
  getCourses,
  getRecommendedCourses,
  getCourseById,
  getCourseCategories,
  getWpCategories,
  getCategoryById,
  getFullAuthorById,
  getFullCourseById,
  getCourseSteps,
  getLessonTopics,
  getCourseLessons,
  getCourseTopics,
  getLessonById,
  getLessonsByIds,
  getTopicById,
  searchCourses,
  isFreeCourse,
  isFreeTopic,
  isFreeCategory,
  isFeaturedCategory,
  tryGetCourseMedia,
  tryGetCourseField,
  tryGetTopicDataForLessonFromCourseSteps,
  tryGetTopicCountForLessonFromCourseSteps,
  tryGetOrderedTopicsForLessonFromCourse,
  COURSE_FIELD_COURSE_LENGTH,
  LMS_TYPE_PREFIX,
  LMS_TYPE_COURSE,
  LMS_TYPE_COURSES,
  LMS_TYPE_LESSON,
  LMS_TYPE_LESSONS,
  LMS_TYPE_QUIZ,
  LMS_TYPE_TOPIC
}
